import { FunctionComponent, HTMLAttributes, ReactNode } from 'react';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

type ChipUIProps = {
  label?: ReactNode,
  size?: 'small' | 'medium',
  variant?: 'filled' | 'outlined',
  color?: ChipColor,
  children?: ReactNode,
} & HTMLAttributes<HTMLDivElement>;

/**
 * Chip.
 *
 * Prop API unchanged. Appearance is now the design system's `.ds-chip`, which
 * is a SOFT chip — a tinted background with matching tinted text — rather than
 * the old solid fill. Soft chips sit much more quietly in a dense table, and
 * they keep their contrast in dark mode, which a solid fill with white text
 * does not.
 *
 * `color` maps onto the design system's tone scale; `default` deliberately
 * carries no tone so it takes the neutral surface treatment.
 */
const TONE: Record<ChipColor, string | undefined> = {
  default: undefined,
  primary: 'accent',
  secondary: 'warn',
  warning: 'warn',
  error: 'danger',
  info: 'info',
  success: 'ok',
};

const ChipUI: FunctionComponent<ChipUIProps> = ({
  label,
  size = 'medium',
  variant = 'filled',
  color = 'default',
  className = '',
  style,
  onClick,
  children,
  ...rest
}) => (
  <div
    className={`ds-chip ${className}`}
    data-ds-tone={TONE[color]}
    data-ds-variant={variant === 'outlined' ? 'outline' : undefined}
    data-ds-size={size === 'medium' ? 'md' : undefined}
    // Call-site style still wins, as before.
    style={{ cursor: onClick ? 'pointer' : undefined, ...style }}
    onClick={onClick}
    {...rest}
  >
    {label ?? children}
  </div>
);

export default ChipUI;
